import React, { useEffect, useRef, useState } from "react";
import classNames from "classnames/bind";
import storiesStyles from "./StoriesSubSlider.module.scss";
import { SwiperSlide } from "swiper/react";
import { useStoriesProviderData } from "../../../providers/storiesProvider";
import { Swiper as SwiperClass } from "swiper/types";
import { StorySlide } from "@core/types";
import { useStoryButton } from "../../../utils/useStoryButton";

type SlideVideoTypeType = {
  buttonSlot?: StorySlide["button"];
  isActive: boolean;
  src: string;
  swiper: SwiperClass;
  id: number;
};

const cx = classNames.bind(storiesStyles);

const WAITING_TIME = 5000;

const SlideVideoType = ({ swiper, buttonSlot, src, isActive, id }: SlideVideoTypeType) => {
  const {
    slideToNext,
    startPaginationAnimating,
    setIsSliderPlaying,
    stopPaginationAnimating,
    setBulletToStartPosition,
    setActiveVideo,
    isVideoMuted,
    nestedSlider,
    setIsSliderLoading,
  } = useStoriesProviderData();
  const { handleStoryButtonClick } = useStoryButton(buttonSlot);
  const videoRef = useRef<HTMLVideoElement>(null);
  const isLoadedBefore = useRef(false);
  const [duration, setDuration] = useState(60000);
  let timer: ReturnType<typeof setTimeout> | null = null;

  const clearTimer = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const checkIsActive = (callback: () => void) => {
    if (!isActive) {
      return;
    }

    callback();
  };

  const onCanPlayHandler = () => {
    if (!videoRef.current) {
      return;
    }

    if (timer) return;

    timer = setTimeout(() => {
      clearTimer();
    }, WAITING_TIME);

    const restDuration = duration - videoRef?.current?.currentTime;

    startPaginationAnimating(swiper, restDuration * 1000);
    setIsSliderPlaying(true);
    videoRef.current.play();
  };

  const onLoadedDataHandler = (e: any) => {
    const target = e.target as HTMLVideoElement;
    isLoadedBefore.current = true;
    setIsSliderLoading(false);

    setDuration(target.duration);
  };

  const onEndedHandler = () => {
    setActiveVideo(null);
    slideToNext();
    if (nestedSlider?.destroyed) return;
    if (swiper.isEnd) {
      setBulletToStartPosition(swiper);
      return;
    }
  };

  const onWaitingHandler = () => {
    if (!videoRef.current) {
      return;
    }
    setIsSliderPlaying(false);
    stopPaginationAnimating();
  };

  const onPauseHandler = () => {
    setIsSliderPlaying(false);
    stopPaginationAnimating();
  };

  const onPlayHandler = () => {
    if (!videoRef.current) {
      return;
    }

    const restDuration = duration - videoRef?.current?.currentTime;
    startPaginationAnimating(swiper, restDuration * 1000);
    setIsSliderPlaying(true);
  };

  useEffect(() => {
    if (isActive) {
      if (!isLoadedBefore.current) {
        setIsSliderLoading(true);
      }
      setActiveVideo(videoRef.current);
      const playPromise = videoRef?.current?.play();
      if (playPromise !== undefined) {
        playPromise
          .then((_) => {
            videoRef?.current?.play();
          })
          .catch((error) => {
            console.log(error, "error");
          });
      }
    } else {
      videoRef?.current?.pause();
      videoRef!.current!.currentTime = 0;
    }
  }, [isActive, setActiveVideo, setIsSliderLoading]);

  const onButtonClick = (e: React.MouseEvent) => {
    handleStoryButtonClick();
  };

  return (
    <SwiperSlide className={cx("slider__item")}>
      <video
        ref={videoRef}
        id={id?.toString()}
        className={cx("video")}
        preload={"none"}
        controls={false}
        autoPlay={false}
        muted={isVideoMuted}
        onEnded={onEndedHandler}
        onPause={() => checkIsActive(onPauseHandler)}
        onCanPlay={() => checkIsActive(onCanPlayHandler)}
        onLoadedData={(e) => checkIsActive(() => onLoadedDataHandler(e))}
        onWaiting={() => checkIsActive(onWaitingHandler)}
        onPlay={() => checkIsActive(onPlayHandler)}
      >
        <source id="mp4" src={src} type="video/mp4" />
      </video>
      {!!buttonSlot && (
        <button onClick={onButtonClick} className={cx("link-button")}>
          {buttonSlot.text}
        </button>
      )}
    </SwiperSlide>
  );
};

SlideVideoType.displayName = "SwiperSlide";

export { SlideVideoType };
